import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Typography } from '@mui/material'

import { RpcError } from '@protobuf-ts/runtime-rpc'
import {
    UserV1UserServiceSetExpiringUserDelegationBindingByAdmin,
    UserV1UserServiceSetExpiringUserDelegationBindingSelf,
} from 'c1gen/permissions'
import { EditSave } from 'components/core/button'
import { APIError } from 'components/core/errors/api'
import { C1TableGrid } from 'components/core/table-grid/C1TableGrid'
import { useHasPermission } from 'components/no-ui/capability'
import { useAlert } from 'hooks/useAlert'
import {
    ExpiringUserDelegationBinding,
    SetExpiringUserDelegationBindingByAdminRequest,
    SetExpiringUserDelegationBindingSelfRequest,
} from 'pbts/c1/api/user/v1/user'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import {
    getExpiringUserDelegationBindingApi,
    setExpiringUserDelegationBindingByAdminApi,
    setExpiringUserDelegationBindingSelfApi,
} from 'redux_toolkit/users'
import * as yup from 'yup'

import { DelegateDisplay } from './user-delegate-display'
import { DelegateFormSections } from './user-delegate-edit-form'
import { DelegationType, UserDelegationFormData } from './user-delegate-types'

type ExpiringDelegatedUserDetailsProps = {
    userId: string
    fromAdmin?: boolean
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSchema = (intl: IntlShape) => yup.object({
    delegatedUserId: yup.string()
        .nullable()
        .when('delegationType', {
            is: (value) => value === 'permanent' || value === 'temporary',
            then: (d) => d.required(intl.formatMessage({
                id: 'user_details_delegate_start_date_required',
                defaultMessage: 'User is required',
            })),
            otherwise: (d) => d.nullable().transform(() => null),
        }),
    startAt: yup.date().nullable(),
    expirationAt: yup.date().nullable(),

    delegationType: yup.mixed<DelegationType>(),
})

const getDelegateType = (binding: ExpiringUserDelegationBinding | undefined): DelegationType => {
    if (!binding) {
        return DelegationType.NO_DELEGATE
    }
    if (binding?.startAt || binding?.expirationAt) {
        return DelegationType.TEMPORARY
    }
    return DelegationType.PERMANENT
}

export const ExpiringDelegatedUserDetails: FC<ExpiringDelegatedUserDetailsProps> = ({
    userId,
    fromAdmin = false,
}) => {
    const intl = useIntl()
    const toast = useAlert()
    const [formError, setFormError] = useState<RpcError | undefined>(undefined)
    const [isEditable, setIsEditable] = useState<boolean>(false)
    const { mutate: updateDelegatedUser } = setExpiringUserDelegationBindingSelfApi()
    const { mutate: updateDelegatedUserByAdmin } = setExpiringUserDelegationBindingByAdminApi()
    const hasAdminPermission = useHasPermission(UserV1UserServiceSetExpiringUserDelegationBindingByAdmin)
    const hasSelfPermission = useHasPermission(UserV1UserServiceSetExpiringUserDelegationBindingSelf)

    const schema = getSchema(intl)

    const {
        data: delegationData,
        isLoading: isDelegateLoading,
        isError: isDelegateError,
    } = getExpiringUserDelegationBindingApi.useGetExpiringUserDelegationBinding({ userId }, {
        notify: true,
    })

    const defaultValues = useMemo(() => {
        return {
            delegatedUserId: delegationData?.item?.delegatedUserId || '',
            startAt: delegationData?.item?.startAt
                ? Timestamp.toDate(delegationData?.item.startAt)
                : null,
            expirationAt: delegationData?.item?.expirationAt
                ? Timestamp.toDate(delegationData?.item.expirationAt)
                : null,
            delegationType: getDelegateType(delegationData?.item) || DelegationType.NO_DELEGATE,
        }
    }, [delegationData])

    const {
        watch,
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        clearErrors,
        reset,
    } = useForm<UserDelegationFormData>({
        resolver: yupResolver(schema) as Resolver<UserDelegationFormData>,
        defaultValues,
    })

    // Store update default form values whenever delegation data changes
    useEffect(() => {
        if (!isEditable && !isDelegateLoading && !isDelegateError) {
            reset(defaultValues)
        }
    }, [delegationData, defaultValues, isDelegateLoading, isDelegateError, reset, isEditable])

    const formValues = watch()

    const onSubmit = useCallback(async (formData: UserDelegationFormData): Promise<void> => {
        setFormError(undefined)
        try {
            let success

            if (fromAdmin && hasAdminPermission) {
                const request = SetExpiringUserDelegationBindingByAdminRequest.create({
                    userId,
                    delegatedUserId: formData.delegatedUserId || '',
                    delegationStartAt: formData.startAt ? Timestamp.fromDate(formData.startAt) : undefined,
                    delegationExpireAt: formData.expirationAt ? Timestamp.fromDate(formData.expirationAt) : undefined,
                })
                success = await updateDelegatedUserByAdmin(request)
            } else if (hasSelfPermission) {
                const request = SetExpiringUserDelegationBindingSelfRequest.create({
                    delegatedUserId: formData.delegatedUserId || '',
                    delegationStartAt: formData.startAt ? Timestamp.fromDate(formData.startAt) : undefined,
                    delegationExpireAt: formData.expirationAt ? Timestamp.fromDate(formData.expirationAt) : undefined,
                })
                success = await updateDelegatedUser(request)
            } else {
                setFormError(new RpcError(intl.formatMessage({
                    id: 'delegate_user_update_permission_error',
                    defaultMessage: 'You do not have permission to update delegate user',
                })))
            }

            if (success) {
                toast(<FormattedMessage
                    id="delegate_user_update_success"
                    defaultMessage="Delegate user updated successfully"
                />, 'success')

                setIsEditable(false)
                // Clear any form errors after successful submit
                clearErrors()
            }
        } catch (err) {
            setFormError(err as RpcError)
        }
    }, [intl,
        toast,
        updateDelegatedUser,
        clearErrors,
        hasAdminPermission,
        updateDelegatedUserByAdmin,
        userId,
        hasSelfPermission,
        fromAdmin,
    ])

    const onCancel = useCallback((): void => {
        // Reset values to original state
        reset(defaultValues)
        setIsEditable(false)
        setFormError(undefined)
        clearErrors()
    }, [clearErrors, defaultValues, reset])

    const onEditClick = useCallback((): void => {
        clearErrors()
        setFormError(undefined)
        setIsEditable(true)
    }, [clearErrors])

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <C1TableGrid
                title={
                    <Stack gap={2}>
                        <FormattedMessage id="delegate_tasks" defaultMessage="Delegate tasks" />
                        <Typography>
                            <FormattedMessage
                                id="out_of_office_delegate_description"
                                defaultMessage="Assign a user to be delegated your tasks."
                            />
                        </Typography>
                    </Stack>
                }
                buttons={<EditSave
                    editable={isEditable}
                    onEditClick={onEditClick}
                    onCancelClick={onCancel}
                    onSaveClick={handleSubmit(onSubmit)}
                    permission={fromAdmin ?
                        UserV1UserServiceSetExpiringUserDelegationBindingByAdmin
                        : UserV1UserServiceSetExpiringUserDelegationBindingSelf
                    }
                />}
            >
                {formError && (
                    <APIError
                        err={formError.message}
                        title={
                            <FormattedMessage
                                id="component_out_of_office_delegate_error"
                                defaultMessage="Error: Failed to update delegate user"
                            />
                        }
                    />
                )}
                <Stack spacing={4}>
                    {isEditable ? (
                        <DelegateFormSections
                            control={control}
                            errors={errors}
                            watch={watch}
                            setValue={setValue}
                            userId={userId}
                            clearErrors={clearErrors}
                        />
                    ) : (
                        <DelegateDisplay formValues={formValues} isError={isDelegateError} isLoading={isDelegateLoading} />
                    )}
                </Stack>
            </C1TableGrid>
        </form>
    )
}
